import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import React, { useRef, useEffect, useImperativeHandle } from 'react';
import { ComboBoxContext } from "./ComboBoxContext";
import styled from 'styled-components';
import { easeCurves } from "../../motion/eases";
import { theme } from "../../theme";
import { motion } from 'framer-motion';
import mergeRefs from 'react-merge-refs';
import { Portal } from "../Portal";
import { useIsomorphicLayoutEffect, usePositioner } from "../../hooks";
var OFFSET = 8;
var StyledPopoverContent = styled(motion.div).withConfig({
  displayName: "ComboBoxPopoverList__StyledPopoverContent",
  componentId: "vapor__sc-19hq6k8-0"
})(["background:", ";border:1px solid ", ";box-shadow:4px 4px 50px rgba(0,0,0,0.4);border-radius:", ";outline:none;position:fixed;display:inline-flex;flex-direction:column;margin-top:", ";min-height:", ";max-height:", ";min-width:", ";max-width:", ";z-index:", ";width:", ";"], theme.colors.palette.grey15, theme.colors.palette.whiteA05, theme.radii[4], theme.spaces[4], theme.spaces[32], function (p) {
  return p.$maxHeight;
}, function (p) {
  return p.$minWidth;
}, function (p) {
  return p.$maxWidth;
}, theme.zIndices.popover, function (p) {
  return p.$width;
});
var StyledPopoverBody = styled.div.withConfig({
  displayName: "ComboBoxPopoverList__StyledPopoverBody",
  componentId: "vapor__sc-19hq6k8-1"
})(["overflow-x:hidden;overflow-y:auto;height:100%;background:", ";padding:", " ", ";"], theme.colors.palette.grey15, theme.spaces[4], theme.spaces[8]);
/**
 * `ComboBoxList`
 * List of comobobox options in a `PopoverContent` component
 * (unused)
 */

/**
 * `ComboBoxList`
 * A Popover with a list of scrollable results.
 */

export var ComboBoxPopoverList = React.forwardRef(function ComboBoxPopoverList(_ref, forwardedRef) {
  var children = _ref.children,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? '168px' : _ref$maxHeight,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? '250px' : _ref$width,
      _ref$animationDuratio = _ref.animationDuration,
      animationDuration = _ref$animationDuratio === void 0 ? 100 : _ref$animationDuratio,
      minWidth = _ref.minWidth,
      maxWidth = _ref.maxWidth,
      targetRef = _ref.targetRef,
      placement = _ref.placement,
      style = _ref.style,
      other = _objectWithoutProperties(_ref, ["children", "maxHeight", "width", "animationDuration", "minWidth", "maxWidth", "targetRef", "placement", "style"]);

  var popoverRef = useRef(null);
  var context = React.useContext(ComboBoxContext);
  var getMenuProps = context.getMenuProps,
      isOpen = context.isOpen,
      getInputProps = context.getInputProps,
      inputRef = context.inputRef,
      itemCount = context.itemCount;
  var visible = isOpen && itemCount > 0;

  var _usePositioner = usePositioner(),
      positionPopover = _usePositioner.positionPopover,
      popperJSRef = _usePositioner.popperJSRef;

  useImperativeHandle(forwardedRef, function () {
    return {
      forceUpdate: function forceUpdate() {
        if (popperJSRef.current) {
          popperJSRef.current.update();
        }
      }
    };
  });
  useEffect(function () {
    var target = targetRef ? targetRef.current : inputRef.current;

    if (isOpen) {
      positionPopover({
        placement: placement,
        offset: [0, OFFSET],
        popoverRef: popoverRef,
        targetEl: target
      });
    }
  }, [isOpen, positionPopover, getInputProps, inputRef, targetRef, placement]);
  useIsomorphicLayoutEffect(function () {
    if (popperJSRef.current) {
      popperJSRef.current.update();
    }
  });

  var _getMenuProps = getMenuProps(),
      ref = _getMenuProps.ref,
      otherProps = _objectWithoutProperties(_getMenuProps, ["ref"]);

  return /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(StyledPopoverContent, _extends({
    ref: mergeRefs([ref, popoverRef]),
    $width: width,
    $minWidth: minWidth,
    $maxWidth: maxWidth,
    $maxHeight: maxHeight,
    $duration: 200,
    style: _objectSpread(_objectSpread({}, style), {}, {
      overflow: 'hidden',
      visibility: visible ? 'visible' : 'hidden',
      opacity: visible ? 1 : 0,
      transition: visible ? "opacity ".concat(animationDuration, "ms ").concat(easeCurves.primary) : 'none',
      margin: 0
    })
  }, otherProps, other), /*#__PURE__*/React.createElement(StyledPopoverBody, null, isOpen && children)));
});
/**
 * `ComboBoxListLabel`
 * Default label for the ComboBoxList
 */

export var ComboBoxListLabel = function ComboBoxListLabel(_ref2) {
  var label = _ref2.label,
      other = _objectWithoutProperties(_ref2, ["label"]);

  var context = React.useContext(ComboBoxContext);

  if (!context) {
    throw new Error('ComboBoxListLabel must be wrapped in a ComboBox component');
  }

  var getLabelProps = context.getLabelProps;
  return /*#__PURE__*/React.createElement("label", _extends({}, getLabelProps(), other), label);
};